import React, { useState, useEffect, useRef } from "react";
import { IoIosAddCircleOutline, IoMdClose } from "react-icons/io";
import { SlOptionsVertical } from "react-icons/sl";
import Swal from "sweetalert2";

import {
  ButtonAction,
  MenuTable,
  MenuTableDivider,
  MenuTableItem,
  Table,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
  TableContainer,
} from "../../../components/Table/styles";
import {
  BlueTitle,
  GrayTitle,
  ModalBody,
  ModalBodyContainer,
  ModalContainer,
  ModalContent,
  ModalFooter,
  StyledHR,
  TextError,
  TitleAndButton,
} from "../../../shared/styles/global.styles";
import { Colors } from "../../../shared/colors/colors";
import lotacaoService from "../../../services/LotacaoService";
import {
  ContainerButton,
  ContainerLotacao,
  MainContainerLotacao,
  SmallText,
} from "./lotacao.styles";
import { Col, Form, ModalHeader, Row } from "react-bootstrap";
import { Label } from "../../../components/Label/styles";
import InputComponent from "../../../components/Input/input";
import {
  GetLotacaoDto,
  CreateLotacaoDto,
  UpdateLotacaoDto,
} from "../../../shared/dtos/Lotacao.dto";
import Button from "../../../components/Button/button";
import { useAuth } from "../../../AuthContext/AuthContext";
import { ModulosEnum } from "../../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../../shared/enum/permicao.enum";
import { IoCloseSharp } from "react-icons/io5";

const Lotacao: React.FC = () => {
  const { checkPermission } = useAuth();
  const editOnly = checkPermission(
    ModulosEnum.LOTACOES,
    PermissoesEnum.ESCRITA
  );
  const [lotacoes, setLotacoes] = useState<GetLotacaoDto[]>([]);
  const [inputLotacao, setInputLotacao] = useState<CreateLotacaoDto>({
    descricao: "",
  });
  const [selectedLotacao, setSelectedLotacao] = useState<GetLotacaoDto | null>(
    null
  );
  const [isRequest, setIsRequest] = useState(false);
  const [isOpenDropdown, setIsOpenDropdown] = useState<number | null>(null);
  const [showModalNewLotacao, setShowModalNewLotacao] =
    useState<boolean>(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const getLotacoes = async () => {
    const response = await lotacaoService.getAllLotacoes();
    setLotacoes(response);
  };

  const handleSubmit = () => {
    if (inputLotacao?.descricao.trim() === "") {
      Swal.fire("Alerta", "A descrição não pode ser enviada vazia.", "warning");
      setInvalidFields(["descricaoLotacao"]);
      return;
    }

    setIsRequest(true);

    Swal.fire({
      title: "Aguarde...",
      text: "Salvando lotação...",
      icon: "info",
      showConfirmButton: false,
      allowOutsideClick: false,
      willOpen: () => {
        Swal.showLoading();
      },
    });

    if (selectedLotacao) {
      const data: UpdateLotacaoDto = {
        ...selectedLotacao,
        descricao: inputLotacao?.descricao,
      };
      lotacaoService
        .updateLotacao(selectedLotacao.id, data)
        .then((response) => {
          Swal.close();
          Swal.fire("Sucesso", "Lotação atualizada com sucesso!", "success");
          getLotacoes(); // Update the table after update
        })
        .catch((error) => {
          console.error("[handleUpdate] Erro ao atualizar lotacao:", error);
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao atualizar lotacao.",
            "error"
          );
        });
    } else {
      const lotacaoExists = lotacoes.find(
        (l) =>
          l.descricao.trim().toLowerCase() ===
          inputLotacao?.descricao.trim().toLowerCase()
      );
      if (lotacaoExists) {
        Swal.fire(
          "Alerta",
          "Já existe uma lotação com essa descrição cadastrada.",
          "warning"
        );
        return;
      }
      lotacaoService
        .createLotacao(inputLotacao as CreateLotacaoDto)
        .then((response) => {
          Swal.close();
          Swal.fire("Sucesso", "Lotação criada com sucesso!", "success");
          getLotacoes(); // Update the table after creation
        })
        .catch((error) => {
          console.error("[handleAdd] Erro ao criar lotacao:", error);
          Swal.close();
          Swal.fire(
            "Erro",
            error instanceof Error
              ? error.message
              : "Erro desconhecido ao criar lotacao.",
            "error"
          );
        });
    }

    handleClear();
    setIsRequest(false);
    setShowModalNewLotacao(false);
  };

  const handleDeleteLotacao = async (id: number) => {
    try {
      Swal.fire({
        title: "Aguarde...",
        text: "Excluindo a lotação...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const result = await Swal.fire({
        title: "Tem certeza?",
        text: "Você realmente deseja excluir esta lotação? Esta ação não pode ser desfeita.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sim, excluir!",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
      });

      if (result.isConfirmed) {
        await lotacaoService.deleteLotacao(id);
        getLotacoes(); // Refresh the list after deletion
        Swal.close();
        Swal.fire(
          "Excluído!",
          "A lotação foi excluída com sucesso.",
          "success"
        );
      } else {
        Swal.close();
      }
    } catch (error) {
      console.error(`[handleDelete] Erro ao excluir ID ${id}:`, error);
      Swal.close();
      Swal.fire(
        "Erro",
        error instanceof Error
          ? error.message
          : "Erro desconhecido ao excluir item.",
        "error"
      );
    }
  };

  const handleClear = () => {
    setInputLotacao({ descricao: "" });
    setSelectedLotacao(null);
  };

  const toggleDropdown = (id: number) => {
    setIsOpenDropdown(id === isOpenDropdown ? null : id);
  };

  const closeNewLotacaoModal = () => {
    setSelectedLotacao(null);
    setShowModalNewLotacao(false);
    setIsRequest(false);
  };

  const clearError = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value } = event.target;

    setInvalidFields((prev) => prev.filter((field) => field !== name));
  };

  useEffect(() => {
    getLotacoes();
  }, []);
  const menuRefsMap = useRef<Map<number, HTMLDivElement | null>>(new Map());
  return (
    <MainContainerLotacao>
      <TitleAndButton>
        <GrayTitle>Lotações</GrayTitle>
        <ContainerButton>
          {editOnly && (
            <Button
              schemaButton="full-primary"
              action={() => {
                setShowModalNewLotacao(true);
                setInputLotacao({ descricao: "" });
              }}
              icon={
                <IoIosAddCircleOutline
                  style={{ fontSize: "1.3rem", marginRight: "0.3rem" }}
                />
              }
              disabled={isRequest && !editOnly}
            >
              Criar nova lotação
            </Button>
          )}
        </ContainerButton>
      </TitleAndButton>
      <StyledHR />
      <TableContainer style={{ flex: 1 }}>
        <Table id="lotacao-table">
          <TableHead>
            <TableRow>
              <TableHeader>Descrição</TableHeader>
              {editOnly && (
                <TableHeader style={{ width: "1px", whiteSpace: "nowrap" }}>
                  Ações
                </TableHeader>
              )}
            </TableRow>
          </TableHead>
          <tbody>
            {lotacoes.length > 0 ? (
              lotacoes.map((lotacao) => (
                <TableRow key={lotacao.id}>
                  <TableData>{lotacao.descricao}</TableData>
                  {editOnly && (
                    <TableData
                      key={`actions-${lotacao.id}`}
                      style={{
                        position: "relative",
                        overflow: "visible",
                      }}
                    >
                      <ButtonAction onClick={() => toggleDropdown(lotacao.id!)}>
                        {isOpenDropdown === lotacao.id ? (
                          <IoMdClose
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        ) : (
                          <SlOptionsVertical
                            cursor={"pointer"}
                            size={20}
                            style={{ color: Colors.primary }}
                          />
                        )}
                      </ButtonAction>
                      <MenuTable
                        show={isOpenDropdown === lotacao.id}
                        align="start"
                        isLastRows={
                          lotacoes.length > 0 &&
                          lotacoes.indexOf(lotacao) >=
                            lotacoes.length - Math.ceil(lotacoes.length / 2)
                        }
                        ref={(el: HTMLDivElement | null) => {
                          // Armazena a referência no mapa usando o ID do registro como chave
                          menuRefsMap.current.set(lotacao.id!, el);
                        }}
                      >
                        <MenuTableItem
                          eventKey="1"
                          onClick={() => {
                            setSelectedLotacao(lotacao);
                            setInputLotacao(lotacao);
                            setIsOpenDropdown(null);
                            setShowModalNewLotacao(true);
                          }}
                        >
                          Editar
                        </MenuTableItem>
                        <MenuTableDivider />
                        <MenuTableItem
                          eventKey="2"
                          onClick={() => {
                            handleDeleteLotacao(lotacao.id!);
                            setIsOpenDropdown(null);
                          }}
                        >
                          Excluir
                        </MenuTableItem>
                      </MenuTable>
                    </TableData>
                  )}
                </TableRow>
              ))
            ) : (
              <tr>
                <TableData colSpan={2} className="text-center">
                  Sem lotação encontrada.
                </TableData>
              </tr>
            )}
          </tbody>
        </Table>
      </TableContainer>

      <ModalContainer show={showModalNewLotacao}>
        <ModalContent width={35}>
          <Button
            type="button"
            schemaButton="x-close"
            action={() => closeNewLotacaoModal()}
            icon={<IoCloseSharp size={20} />}
            position="absolute"
            top={10}
            right={10}
          />
          <ModalHeader>
            <BlueTitle>
              {!!selectedLotacao ? "Editar " : "Cadastrar nova "}
              lotação
            </BlueTitle>
          </ModalHeader>
          <ModalBody>
            <Label htmlFor="tipoRemessa">
              Descrição da lotação <TextError>*</TextError>
            </Label>
            <div>
              <InputComponent
                type="text"
                name="descricaoLotacao"
                id="descricaoLotacao"
                value={inputLotacao?.descricao ?? ""}
                onChange={(e) => {
                  if (e.target.value.length <= 50) {
                    setInputLotacao({
                      descricao: e.target.value,
                    });
                    clearError(e);
                  }
                }}
                placeholder="Descrição da lotação"
                disabled={isRequest}
                color="black"
                width={Number("100%")}
                maxLength={50}
                errorMessage={
                  invalidFields.includes("descricaoLotacao")
                    ? "Descrição da lotação é obrigatório"
                    : ""
                }
              />
              <SmallText style={{ textAlign: "right", marginTop: "4px" }}>
                Total de caracteres {inputLotacao?.descricao?.length ?? 0}/50
              </SmallText>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              schemaButton="full-primary"
              action={() => {
                handleSubmit();
              }}
              disabled={isRequest || inputLotacao.descricao === ""}
            >
              {!!selectedLotacao ? "Salva edição" : "Cadastrar nova"} lotação
            </Button>
          </ModalFooter>
        </ModalContent>
      </ModalContainer>
    </MainContainerLotacao>
  );
};

export default Lotacao;
